import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { FiRefreshCw, FiUsers, FiDownload } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Logo from '../common/Logo';
import { getLearningSpeedData, LearningSpeedUser, LearningSpeedResponse } from '../../api/admin';
import { convertToCSV, downloadCSV, formatDateForFilename } from '../../utils/exportUtils';

// Access denied component for non-admin users
const AccessDenied = () => (
  <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col justify-center p-6">
    <div className="max-w-4xl mx-auto text-center">
      <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
      <p className="text-base text-gray-600 mt-2">You are not authorized to view this page.</p>
    </div>
  </div>
);

// Header component
const LearningSpeedHeader = () => (
  <h1 className="text-3xl font-bold text-center pt-4 mb-4 flex items-center justify-center gap-3">
    <Logo size={28} />
    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
      Learning Speed Dashboard
    </span>
  </h1>
);

// Controls component with age grade filter
interface ControlsProps {
  ageGradeFilter: number | null;
  onAgeGradeFilterChange: (grade: number | null) => void;
  onRefresh: () => void;
  onDownloadCSV: () => void;
  isLoading?: boolean;
  hasData?: boolean;
}

const Controls: React.FC<ControlsProps> = ({
  ageGradeFilter,
  onAgeGradeFilterChange,
  onRefresh,
  onDownloadCSV,
  isLoading = false,
  hasData = false
}) => {
  // Available grade options (0-12 based on typical school grades)
  const gradeOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  return (
    <Card className="shadow-lg border border-gray-100 mb-4">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label htmlFor="ageGradeFilter" className="block text-sm font-medium text-gray-700 mb-2">
              Age Grade Filter
            </label>
            <select
              id="ageGradeFilter"
              value={ageGradeFilter || ''}
              onChange={(e) => onAgeGradeFilterChange(e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            >
              <option value="">All Grades</option>
              {gradeOptions.map(grade => (
                <option key={grade} value={grade}>Grade {grade}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={onRefresh}
              disabled={isLoading}
              variant="outline"
              className="flex items-center px-4"
            >
              <FiRefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={onDownloadCSV}
              disabled={isLoading || !hasData}
              variant="outline"
              className="flex items-center px-4 bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
            >
              <FiDownload className="w-4 h-4 mr-2" />
              CSV
            </Button>
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <p>📅 Data Range: September 1, 2025 - Present</p>
        </div>
      </CardContent>
    </Card>
  );
};

// Summary stats component
interface SummaryStatsProps {
  totalUsers: number;
  statusCounts: {
    [status: string]: number;
  };
  timeUsageCounts: {
    spendingEnoughTime: number;
    notSpendingEnoughTime: number;
  };
  isLoading?: boolean;
}

const SummaryStats: React.FC<SummaryStatsProps> = ({
  totalUsers,
  statusCounts,
  timeUsageCounts,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <div className="space-y-6 mb-8">
        <Card className="shadow-lg border border-gray-100">
          <CardContent className="p-4">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-300 rounded mb-4"></div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="text-center">
                    <div className="h-8 bg-gray-300 rounded mb-2"></div>
                    <div className="h-4 bg-gray-300 rounded"></div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusDescriptions = {
    'All grades mastered': 'Student has achieved fluency in all math tracks',
    'Above age grade': 'Student is working above their age level',
    'On-track': 'Good progress - score matches time invested',
    'Off-track': 'Needs more practice - score below expected for time spent',
    'Needs attention': 'Various factors require review',
    '-': 'No assessment data available',
    'Error - Age Grade Missing': 'Missing age grade information'
  };

  const statusColors = {
    'All grades mastered': 'text-purple-600',
    'Above age grade': 'text-green-600',
    'On-track': 'text-blue-600',
    'Off-track': 'text-yellow-600',
    'Needs attention': 'text-orange-600',
    '-': 'text-gray-600',
    'Error - Age Grade Missing': 'text-red-600'
  };

  // Sort statuses for consistent display, excluding unwanted ones
  const sortedStatuses = Object.keys(statusCounts)
    .filter(status => status !== '-' && status !== 'Error - Age Grade Missing')
    .sort((a, b) => {
      const order = ['All grades mastered', 'Above age grade', 'On-track', 'Off-track', 'Needs attention'];
      return order.indexOf(a) - order.indexOf(b);
    });

  return (
    <div className="space-y-4 mb-8">
      {/* Top Row: Total Users and Time Usage */}
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Overview</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Total Users */}
            <div className="text-center p-3 rounded-lg bg-gray-50">
              <p className="text-xl font-bold text-blue-600">
                {totalUsers.toLocaleString()}
              </p>
              <p className="text-xs font-medium text-gray-700 mb-1">
                Total Users
              </p>
              <p className="text-xs text-gray-500">
                All students in the system
              </p>
            </div>

            {/* Time Usage Cards */}
            <div className="text-center p-3 rounded-lg bg-gray-50">
              <p className="text-xl font-bold text-green-600">
                {timeUsageCounts.spendingEnoughTime}
              </p>
              <p className="text-xs font-medium text-gray-700 mb-1">
                Spending enough time
              </p>
              <p className="text-xs text-gray-500">
                {totalUsers > 0 ? ((timeUsageCounts.spendingEnoughTime / totalUsers) * 100).toFixed(1) : '0.0'}% • 50+ mins per week (~7.14 mins/day)
              </p>
            </div>

            <div className="text-center p-3 rounded-lg bg-gray-50">
              <p className="text-xl font-bold text-red-600">
                {timeUsageCounts.notSpendingEnoughTime}
              </p>
              <p className="text-xs font-medium text-gray-700 mb-1">
                Not spending enough time
              </p>
              <p className="text-xs text-gray-500">
                {totalUsers > 0 ? ((timeUsageCounts.notSpendingEnoughTime / totalUsers) * 100).toFixed(1) : '0.0'}% • Less than 50 mins per week
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status Breakdown */}
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Breakdown</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {sortedStatuses.map((status) => {
              const count = statusCounts[status] || 0;
              const percentage = totalUsers > 0 ? ((count / totalUsers) * 100).toFixed(1) : '0.0';
              const color = statusColors[status as keyof typeof statusColors] || 'text-gray-600';
              
              return (
                <div key={status} className="text-center p-3 rounded-lg bg-gray-50">
                  <p className={`text-xl font-bold ${color}`}>
                    {count}
                  </p>
                  <p className="text-xs font-medium text-gray-700 mb-1">
                    {status}
                  </p>
                  <p className="text-xs text-gray-500">
                    {percentage}% • {statusDescriptions[status as keyof typeof statusDescriptions] || 'No description available'}
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Learning speed table component
interface LearningSpeedTableProps {
  users: LearningSpeedUser[];
  isLoading?: boolean;
}

const LearningSpeedTable: React.FC<LearningSpeedTableProps> = ({
  users,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-300 rounded w-1/3 mb-6"></div>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-300 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (users.length === 0) {
    return (
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-6 text-center">
          <div className="text-gray-400 mb-4">📊</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data Found</h3>
          <p className="text-gray-600">
            No learning speed data found for the selected criteria.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg border border-gray-100">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Age Grade
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Knowledge Grade
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time Used
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score Progress
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user, index) => (
                <tr key={`${user.userEmail}-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {user.userEmail}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.ageGrade !== null ? `Grade ${user.ageGrade}` : 'N/A'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.knowledgeGrade !== null ? `Grade ${user.knowledgeGrade}` : 'N/A'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.isSpendingEnoughTime === true 
                        ? 'bg-green-100 text-green-800'
                        : user.isSpendingEnoughTime === false
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {user.allocatedTimeHours > 0 
                        ? `${Math.round((user.totalTimeHours / user.allocatedTimeHours) * 100)}% (${user.totalTimeHours.toFixed(1)} / ${user.allocatedTimeHours.toFixed(1)} hrs)`
                        : `${user.totalTimeHours.toFixed(1)} hrs`
                      }
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      typeof user.highestScore === 'number' && typeof user.scoreTarget === 'number' && user.scoreTarget > 0 && user.allocatedTimeHours > 0
                        ? (() => {
                            const scoreProgressPercent = (user.highestScore / user.scoreTarget) * 100;
                            const timeSpentPercent = (user.totalTimeHours / user.allocatedTimeHours) * 100;
                            return scoreProgressPercent >= timeSpentPercent
                              ? 'bg-blue-100 text-blue-800'     // On-track: score progress >= time spent
                              : 'bg-yellow-100 text-yellow-800'; // Off-track: score progress < time spent
                          })()
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {typeof user.highestScore === 'number' && typeof user.scoreTarget === 'number' && user.scoreTarget > 0
                        ? `${Math.round((user.highestScore / user.scoreTarget) * 100)}% (${user.highestScore} / ${user.scoreTarget})`
                        : 'N/A'
                      }
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      !user.status 
                        ? 'bg-gray-100 text-gray-600'
                        : user.status === 'All grades mastered'
                        ? 'bg-purple-100 text-purple-800'
                        : user.status === 'Above age grade' 
                        ? 'bg-green-100 text-green-800'
                        : user.status === 'On-track'
                        ? 'bg-blue-100 text-blue-800'
                        : user.status === 'Off-track'
                        ? 'bg-yellow-100 text-yellow-800'
                        : user.status === 'Needs attention'
                        ? 'bg-orange-100 text-orange-800'
                        : user.status === '-'
                        ? 'bg-gray-100 text-gray-600'
                        : user.status.startsWith('Error')
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {user.status || 'Unknown'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// Error component
interface ErrorDisplayProps {
  error: string;
  onRetry: () => void;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error, onRetry }) => (
  <Card className="shadow-lg border border-red-100">
    <CardContent className="p-6 text-center">
      <div className="text-red-400 mb-4">⚠️</div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Data</h3>
      <p className="text-gray-600 mb-4">{error}</p>
      <Button onClick={onRetry} className="bg-red-600 hover:bg-red-700 text-white">
        <FiRefreshCw className="w-4 h-4 mr-2" />
        Try Again
      </Button>
    </CardContent>
  </Card>
);

// Main component
export const LearningSpeedDashboard: React.FC = () => {
  const { user: currentUser } = useAuth();
  
  // Check if user is admin
  const isAdmin = useMemo(() =>
    currentUser?.role === 'admin',
    [currentUser?.role]
  );

  // State
  const [data, setData] = useState<LearningSpeedResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ageGradeFilter, setAgeGradeFilter] = useState<number | null>(null);

  // Filter data based on age grade filter
  const filteredUsers = useMemo(() => {
    if (!data?.users) return [];
    
    if (ageGradeFilter === null) {
      return data.users;
    }
    
    return data.users.filter(user => user.ageGrade === ageGradeFilter);
  }, [data, ageGradeFilter]);

  // Calculate summary stats from filtered data
  const summaryStats = useMemo(() => {
    if (filteredUsers.length === 0) {
      return {
        totalUsers: 0,
        statusCounts: {},
        timeUsageCounts: {
          spendingEnoughTime: 0,
          notSpendingEnoughTime: 0
        }
      };
    }

    // Count users by status
    const statusCounts: { [status: string]: number } = {};
    let spendingEnoughTime = 0;
    let notSpendingEnoughTime = 0;
    
    filteredUsers.forEach(user => {
      const status = user.status || 'Unknown';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
      
      // Count time usage
      if (user.isSpendingEnoughTime === true) {
        spendingEnoughTime++;
      } else if (user.isSpendingEnoughTime === false) {
        notSpendingEnoughTime++;
      }
    });

    return {
      totalUsers: filteredUsers.length,
      statusCounts,
      timeUsageCounts: {
        spendingEnoughTime,
        notSpendingEnoughTime
      }
    };
  }, [filteredUsers]);

  // Fetch data function
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await getLearningSpeedData();
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch learning speed data';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Retry function
  const handleRetry = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // CSV download function
  const handleDownloadCSV = useCallback(() => {
    if (!filteredUsers || filteredUsers.length === 0) return;

    // Define CSV headers
    const headers = [
      { key: 'userEmail' as keyof LearningSpeedUser, label: 'User Email' },
      { key: 'ageGrade' as keyof LearningSpeedUser, label: 'Age Grade' },
      { key: 'knowledgeGrade' as keyof LearningSpeedUser, label: 'Knowledge Grade' },
      { key: 'totalTimeHours' as keyof LearningSpeedUser, label: 'Total Time (hours)' },
      { key: 'allocatedTimeHours' as keyof LearningSpeedUser, label: 'Allocated Time (hours)' },
      { key: 'highestScore' as keyof LearningSpeedUser, label: 'Highest Score' },
      { key: 'scoreTarget' as keyof LearningSpeedUser, label: 'Score Target' },
      { key: 'status' as keyof LearningSpeedUser, label: 'Status' }
    ];

    // Transform data for CSV with formatted values
    const csvData = filteredUsers.map(user => ({
      ...user,
      ageGrade: user.ageGrade !== null ? user.ageGrade : 'N/A',
      knowledgeGrade: user.knowledgeGrade !== null ? user.knowledgeGrade : 'N/A',
    }));

    const csvContent = convertToCSV(csvData, headers);
    const gradeFilter = ageGradeFilter ? `-grade${ageGradeFilter}` : '';
    const filename = `learning-speed-dashboard${gradeFilter}-${formatDateForFilename()}.csv`;
    
    downloadCSV(csvContent, filename);
  }, [filteredUsers, ageGradeFilter]);

  // Load data on component mount
  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin, fetchData]);

  // Early return if not admin
  if (!isAdmin) {
    return <AccessDenied />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-4">
      <div className="max-w-7xl mx-auto space-y-4">
        <LearningSpeedHeader />
        
        {/* Navigation */}
        <div className="flex mb-4">
          <Link 
            to="/admin" 
            className="text-blue-600 hover:text-blue-800 transition-colors duration-300"
          >
            ← Back to Admin Dashboard
          </Link>
        </div>

        {/* Controls */}
        <Controls
          ageGradeFilter={ageGradeFilter}
          onAgeGradeFilterChange={setAgeGradeFilter}
          onRefresh={fetchData}
          onDownloadCSV={handleDownloadCSV}
          isLoading={isLoading}
          hasData={filteredUsers.length > 0}
        />

        {/* Error Display */}
        {error && (
          <ErrorDisplay error={error} onRetry={handleRetry} />
        )}

        {/* Dashboard Content */}
        {!error && (
          <>
            {/* Summary Statistics */}
            <SummaryStats
              totalUsers={summaryStats.totalUsers}
              statusCounts={summaryStats.statusCounts}
              timeUsageCounts={summaryStats.timeUsageCounts}
              isLoading={isLoading}
            />

            {/* Learning Speed Table */}
            <LearningSpeedTable
              key={`table-${ageGradeFilter || 'all'}-${filteredUsers.length}`}
              users={filteredUsers}
              isLoading={isLoading}
            />
          </>
        )}
      </div>
    </div>
  );
};